import React from 'react';
import { Link } from 'react-router-dom';
import { Case } from '../../types';
import { 
  UserIcon, 
  CalendarIcon, 
  ChatBubbleLeftRightIcon,
  PlayIcon,
  TrashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface CaseCardProps {
  caseData: Case;
  onDelete?: (caseId: string, title: string) => void;
  isDeleting?: boolean;
  onConfirmDelete?: (caseId: string) => void;
  onCancelDelete?: () => void;
}

const CaseCard: React.FC<CaseCardProps> = ({ caseData, onDelete, isDeleting, onConfirmDelete, onCancelDelete }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Helper function to extract key diagnosis info
  const getShortDiagnosis = (diagnosis: string | undefined): string => {
    if (!diagnosis) return 'No diagnosis provided';
    // Extract the first sentence or up to 100 characters
    const firstSentence = diagnosis.split('.')[0];
    if (firstSentence.length > 100) {
      return firstSentence.substring(0, 100) + '...';
    }
    return firstSentence;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden">
      {/* Header Section */}
      <div className="p-6 pb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {caseData.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {caseData.scenario}
        </p>
      </div>

      {/* Diagnosis Section - Redesigned */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 border-y border-blue-100">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-blue-800 mb-0.5">Diagnosis</p>
            <p className="text-sm text-blue-900 line-clamp-2">
              {getShortDiagnosis(caseData.patientProfile.diagnosis)}
            </p>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="p-6 pt-4">
        {/* Creator Badge - Prominent display */}
        {caseData.createdBy && (
          <div className="mb-3">
            <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border border-purple-200">
              <UserIcon className="h-3.5 w-3.5 mr-1.5" />
              <span className="font-semibold">{caseData.createdBy.name}</span>
              {caseData.createdBy.role && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-xs bg-white/60">
                  {caseData.createdBy.role}
                </span>
              )}
            </div>
            {/* Learner Level Badge */}
            {caseData.learnerLevel && (
              <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                {caseData.learnerLevel === 'BEGINNER' && '🌱 Beginner'}
                {caseData.learnerLevel === 'INTERMEDIATE' && '📚 Intermediate'}
                {caseData.learnerLevel === 'ADVANCED' && '🎓 Advanced'}
              </span>
            )}
          </div>
        )}
        
        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center">
            <UserIcon className="h-4 w-4 mr-1" />
            <span>{caseData.patientProfile.name}</span>
          </div>
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 mr-1" />
            <span>{formatDate(caseData.createdAt)}</span>
          </div>
          {caseData._count?.dialogues ? (
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
              <span>{caseData._count.dialogues} dialogue(s)</span>
            </div>
          ) : null}
          {caseData._count?.versions !== undefined && (
            <div className="flex items-center">
              <ClockIcon className="h-4 w-4 mr-1" />
              <span 
                className={`${
                  caseData._count.versions > 1 
                    ? 'text-blue-600 font-medium' 
                    : 'text-gray-400'
                }`}
                title={`${caseData._count.versions} version${caseData._count.versions !== 1 ? 's' : ''}`}
              >
                {caseData._count.versions > 1 
                  ? `${caseData._count.versions} versions` 
                  : 'No updates'}
              </span>
            </div>
          )}
        </div>

        {isDeleting ? (
          // Inline confirmation UI
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800 mb-3">
              <strong>Delete "{caseData.title}"?</strong><br/>
              All your progress will be lost. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => onConfirmDelete?.(caseData.caseId)}
                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Yes, Delete
              </button>
              <button
                onClick={onCancelDelete}
                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Link
              to={`/cases/${caseData.caseId}`}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              View Case
            </Link>
            {onDelete && (
              <button
                onClick={() => onDelete(caseData.caseId, caseData.title)}
                className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseCard;
